// SPDX-License-Identifier: AGPL-3.0-or-later

import ChannelAnnouncementStore from '@app/features/channel/announcements/ChannelAnnouncementStore';
import styles from '@app/features/channel/announcements/components/ChannelAnnouncementBanner.module.css';
import type {Channel} from '@app/features/channel/models/Channel';
import Permission from '@app/features/permissions/state/Permission';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import {
	CaretDownIcon,
	CaretUpIcon,
	MegaphoneIcon,
	PencilSimpleIcon,
	PushPinIcon,
	TrashIcon,
} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useState} from 'react';

interface ChannelAnnouncementBannerProps {
	channel: Channel;
}

export const ChannelAnnouncementBanner: React.FC<ChannelAnnouncementBannerProps> = observer(
	({channel}) => {
		const announcement = ChannelAnnouncementStore.getAnnouncement(channel);
		const isCollapsed = ChannelAnnouncementStore.isCollapsed(channel.id);
		const [isEditing, setIsEditing] = useState(false);
		const [editText, setEditText] = useState(announcement?.text ?? '');
		const [isSaving, setIsSaving] = useState(false);

		const canManage = Boolean(
			channel.guildId &&
				(Permission.can(Permissions.MANAGE_CHANNELS, channel) ||
					Permission.can(Permissions.MANAGE_MESSAGES, channel)),
		);

		if (!announcement || !announcement.pinned) {
			return null;
		}

		const handleSaveEdit = async (e: React.FormEvent) => {
			e.preventDefault();
			setIsSaving(true);
			try {
				if (!editText.trim()) {
					await ChannelAnnouncementStore.deleteAnnouncement(channel.id);
					ToastCommands.success('Announcement removed');
				} else {
					await ChannelAnnouncementStore.setAnnouncement(channel.id, editText.trim(), true);
					ToastCommands.success('Announcement updated for all members');
				}
				setIsEditing(false);
			} catch {
				ToastCommands.error('Failed to update announcement. Please check channel permissions.');
			} finally {
				setIsSaving(false);
			}
		};

		const handleDismiss = async () => {
			setIsSaving(true);
			try {
				await ChannelAnnouncementStore.deleteAnnouncement(channel.id);
				ToastCommands.success('Announcement unpinned');
			} catch {
				ToastCommands.error('Failed to unpin announcement.');
			} finally {
				setIsSaving(false);
			}
		};

		if (isCollapsed && !isEditing) {
			return (
				<div className={styles.container}>
					<div
						className={styles.collapsedBar}
						onClick={() => ChannelAnnouncementStore.toggleCollapsed(channel.id)}
						role="button"
						tabIndex={0}
					>
						<div className={styles.collapsedLeft}>
							<span className={styles.pillBadge}>
								<PushPinIcon size={12} weight="fill" />
								<span>Announcement</span>
							</span>
							<span className={styles.previewText}>{announcement.text}</span>
						</div>
						<div className={styles.collapsedRight}>
							<span>Expand</span>
							<CaretDownIcon size={14} weight="bold" />
						</div>
					</div>
				</div>
			);
		}

		return (
			<div className={styles.container}>
				<div className={styles.expandedCard}>
					<div className={styles.cardHeader}>
						<div className={styles.cardHeaderLeft}>
							<MegaphoneIcon size={18} weight="fill" color="var(--interactive-active, #7983f5)" />
							<span className={styles.cardTitle}>Channel Announcement</span>
							{announcement.authorTag && (
								<span className={styles.authorTag}>• by {announcement.authorTag}</span>
							)}
						</div>

						<div className={styles.cardActions}>
							{canManage && !isEditing && (
								<>
									<button
										type="button"
										className={styles.actionButton}
										title="Edit announcement"
										disabled={isSaving}
										onClick={() => {
											setEditText(announcement.text);
											setIsEditing(true);
										}}
									>
										<PencilSimpleIcon size={15} />
									</button>
									<button
										type="button"
										className={styles.actionButton}
										title="Unpin announcement"
										disabled={isSaving}
										onClick={handleDismiss}
									>
										<TrashIcon size={15} />
									</button>
								</>
							)}
							<button
								type="button"
								className={styles.actionButton}
								title="Collapse banner"
								onClick={() => ChannelAnnouncementStore.toggleCollapsed(channel.id)}
							>
								<CaretUpIcon size={15} weight="bold" />
							</button>
						</div>
					</div>

					{isEditing ? (
						<form className={styles.editContainer} onSubmit={handleSaveEdit}>
							<textarea
								className={styles.editTextarea}
								value={editText}
								disabled={isSaving}
								onChange={(e) => setEditText(e.target.value)}
								placeholder="Enter sticky channel announcement text..."
								rows={3}
							/>
							<div className={styles.editActionsRow}>
								<button
									type="button"
									className={styles.btnSecondary}
									disabled={isSaving}
									onClick={() => setIsEditing(false)}
								>
									Cancel
								</button>
								<button type="submit" className={styles.btnPrimary} disabled={isSaving || !editText.trim()}>
									Save
								</button>
							</div>
						</form>
					) : (
						<div className={styles.cardBody}>{announcement.text}</div>
					)}
				</div>
			</div>
		);
	},
);

export default ChannelAnnouncementBanner;
